import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface GrupoDespesas {
  id: number;
  descricao?: string;
  referencia?: string;
  centroCustoId?: number | null;
  contaContabilId?: number | null;
  isInativo: boolean;
}

export interface GrupoDespesasFormData {
  descricao?: string;
  referencia?: string;
  centroCustoId?: number | null;
  contaContabilId?: number | null;
  isInativo?: boolean;
}

export function useGruposDespesas() {
  const queryClient = useQueryClient();

  const { data: grupos = [], isLoading } = useQuery({
    queryKey: ["grupos-despesas"],
    queryFn: async () => {
      const { data } = await apiClient.get<GrupoDespesas[]>("/api/grupos-despesas");
      return data;
    },
  });

  const createGrupo = useMutation({
    mutationFn: async (formData: GrupoDespesasFormData) => {
      const { data } = await apiClient.post<GrupoDespesas>("/api/grupos-despesas", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grupos-despesas"] });
      toast.success("Grupo criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar grupo: " + error.message);
    },
  });

  const updateGrupo = useMutation({
    mutationFn: async ({ id, ...formData }: GrupoDespesasFormData & { id: number }) => {
      const { data } = await apiClient.put<GrupoDespesas>(`/api/grupos-despesas/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grupos-despesas"] });
      toast.success("Grupo atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar grupo: " + error.message);
    },
  });

  const deleteGrupo = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/grupos-despesas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grupos-despesas"] });
      toast.success("Grupo inativado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar grupo: " + error.message);
    },
  });

  return { grupos, isLoading, createGrupo, updateGrupo, deleteGrupo };
}
